import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Alert, Col, Container, Row } from "reactstrap";
import { fetchPosCompanies } from "../../../api/requests/pos-analytics-requests";
import PosScopeTable from "./PosScopeTable";
import {
    Loading,
    Pager,
    PosBreadcrumb,
    PosFilterBar,
    PosPageHeader,
    POS_REPORT_ROOT,
    SectionTitle,
    StatCard,
    fmtMoney2,
    providerLabelOf,
    trNum,
    useIsSuperAdmin,
    usePosDateRange,
    usePosFilters,
    usePosSort,
} from "./PosReportShared";

import { useAutoRefresh, AUTO_REFRESH_MS } from "../../../hooks/useAutoRefresh";
import RefreshIndicator from "../../../components/Common/RefreshIndicator";
/**
 * Gelişmiş filtreleme alanları — tablodaki kolonlardan sayfaya uygun olanlar.
 * Sayfalı liste olduğu için değerler backend'e gider (bkz. PosScopeFilterDTO).
 */
const FILTER_FIELDS = [
    { key: "search", type: "text", label: "Firma Adı", placeholder: "Firma adında ara" },
    { key: "transactionCount", type: "range", label: "İşlem Sayısı",
      minKey: "minTransactionCount", maxKey: "maxTransactionCount" },
    { key: "grossAmount", type: "range", label: "İşlem Tutarı (₺)",
      minKey: "minGrossAmount", maxKey: "maxGrossAmount" },
    { key: "netAmount", type: "range", label: "Net Tutar (₺)",
      minKey: "minNetAmount", maxKey: "maxNetAmount" },
];

/**
 * POS Rapor → Sağlayıcı detayı: sağlayıcıyı kullanan firmaların listesi.
 * Satıra tıklanınca firma detayına (Cari Bazlı / İşlem Bazlı sekmeleri) inilir.
 *
 * Tasarım notu gereği burada POS sağlayıcı filtresi yoktur (sağlayıcı zaten
 * route'tan gelir); tarih aralığının yanında tablo kolonlarına dayalı gelişmiş
 * filtreleme bulunur.
 */
export default function PosProviderCompaniesPage() {
    const { provider } = useParams();
    const navigate = useNavigate();
    const { startDate, endDate, apply, rangeQuery } = usePosDateRange();
    const { isSuperAdmin } = useIsSuperAdmin();

    const [page, setPage] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [data, setData] = useState(null);

    // Liste sayfalı geldiği için sıralama backend'de yapılır: kolon tıklaması
    // görünen sayfayı değil tüm kapsamı sıralamalı.
    const { sort, direction, toggle } = usePosSort("grossAmount", "desc");
    const onSort = (key) => { setPage(0); toggle(key); };

    // Filtreler de sunucuda uygulanır; her değişiklikte ilk sayfaya dönülür.
    const { draft, applied, set, commit, reset, activeCount } = usePosFilters();
    const onFilterChange = (key, value, immediate) => {
        if (immediate) setPage(0);
        set(key, value, immediate);
    };
    const onFilterCommit = () => { setPage(0); commit(); };

    /**
     * @param {{silent?: boolean}} [opts] silent=true otomatik yenilemede kullanılır:
     * tam sayfa spinner'ı açılmaz, hata basılmaz; filtre/sayfa/sıralama korunur.
     */
    const load = useCallback(async ({ silent = false } = {}) => {
        if (!silent) {
            setLoading(true);
            setError(null);
        }
        try {
            const res = await fetchPosCompanies(provider, {
                startDate, endDate, page, size: 20, sort, direction, ...applied,
            });
            if (res?.statusCode === 200 && res.data) setData(res.data);
            else if (!silent && res?.statusCode === 403) setError("Bu sayfayı görüntüleme yetkiniz yok.");
            else if (!silent) setError(res?.message || "Firma listesi alınamadı.");
        } catch (e) {
            if (!silent) setError("Veri yüklenirken bir hata oluştu.");
        } finally {
            if (!silent) setLoading(false);
        }
    }, [provider, startDate, endDate, page, sort, direction, applied]);

    // load o anki filtre/sayfa/sıralamayı kapsadığı için sessiz çağrı seçimleri bozmaz.
    const autoRefresh = useAutoRefresh(() => load({ silent: true }), { intervalMs: AUTO_REFRESH_MS });

    useEffect(() => {
        load();
    }, [load]);

    const label = data?.providerLabel || providerLabelOf(provider);
    const s = data?.summary;
    const t = data?.totals;

    return (
        <div className="page-content">
            <Container fluid>
                <PosBreadcrumb
                    backTo={POS_REPORT_ROOT}
                    items={[
                        { label: "POS Rapor", to: POS_REPORT_ROOT },
                        { label },
                    ]}
                />
                <PosPageHeader
                    title={label}
                    subtitle={data?.periodLabel ? `Firmalar · ${data.periodLabel}` : "Firmalar"}
                    right={<RefreshIndicator {...autoRefresh} onClick={autoRefresh.refreshNow} />}
                />

                <PosFilterBar
                    startDate={startDate}
                    endDate={endDate}
                    onRangeChange={(s2, e2) => { setPage(0); apply(s2, e2); }}
                    fields={FILTER_FIELDS}
                    values={draft}
                    onChange={onFilterChange}
                    onCommit={onFilterCommit}
                    onReset={() => { setPage(0); reset(); }}
                    activeCount={activeCount}
                />

                {error && <Alert color="danger">{error}</Alert>}

                {loading ? (
                    <Loading />
                ) : (
                    <>
                        <Row className="g-3 mb-3">
                            <Col xs={6} md={3}>
                                <StatCard accent label="Çekilen Toplam" value={fmtMoney2(t?.grossAmount)} />
                            </Col>
                            <Col xs={6} md={3}>
                                <StatCard
                                    label="İşlem Sayısı"
                                    value={trNum(t?.transactionCount)}
                                    sub={`${trNum(s?.failedCount)} başarısız · ${trNum(s?.pendingCount)} bekleyen`}
                                />
                            </Col>
                            <Col xs={6} md={3}>
                                <StatCard
                                    // Kesilen toplam komisyon: firmanın gideri. Süper admin için
                                    // "hakediş" değildir — platformun payı Komisyon Kazancı kolonudur.
                                    label={isSuperAdmin ? "Toplam Komisyon" : "Komisyon Kesintisi"}
                                    value={fmtMoney2(t?.commissionAmount)}
                                />
                            </Col>
                            <Col xs={6} md={3}>
                                <StatCard label="Net Tutar" value={fmtMoney2(t?.netAmount)} />
                            </Col>
                        </Row>

                        <SectionTitle>Firmalar</SectionTitle>
                        <PosScopeTable
                            rows={data?.content || []}
                            totals={t}
                            showAdminColumns={isSuperAdmin}
                            emptyText="Bu dönemde bu POS üzerinden işlem yapan firma bulunmuyor."
                            identityColumns={[
                                {
                                    key: "companyName",
                                    label: "Firma Adı",
                                    className: "fw-semibold text-dark",
                                    sortKey: "companyName",
                                    render: (r) => r.companyName,
                                },
                            ]}
                            sort={sort}
                            direction={direction}
                            onSort={onSort}
                            onRowClick={(r) => navigate(`${POS_REPORT_ROOT}/${provider}/${r.companyId}${rangeQuery}`)}
                            footer={
                                <Pager
                                    page={data?.page ?? 0}
                                    totalPages={data?.totalPages}
                                    totalElements={data?.totalElements}
                                    onChange={setPage}
                                />
                            }
                        />
                    </>
                )}
            </Container>
        </div>
    );
}
